import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch } from "../../hooks/useStore";
import { useForm } from "react-hook-form";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  Building,
  Truck,
  Shield,
  Users,
} from "lucide-react";
import { login, clearError } from "../../store/slices/authSlice";
import { useAuth } from "../../hooks/useAuth";
import type { LoginCredentials } from "../../types";
import Button from "../../components/UI/Button";
import { toast } from "react-toastify";
import { loginSchema } from "../../schemavalidations";
import { yupResolver } from "@hookform/resolvers/yup";
import { AuthHeroSection } from "../../components/auth/AuthHeroSection";
import { getPairedTruck, DEVICE_ROUTE } from "../../utils/deviceSession";
import { useTranslation } from "react-i18next";
import { translateValidation } from "../../utils/translateValidation";
import { vehicleLabel } from "../../utils/vehicleLabel";

const LoginPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, error, user } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: yupResolver(loginSchema),
  });

  const from =
    location.state?.from?.pathname ||
    (user?.role === "driver" ? "/driver-home" : "/dashboard");

  /**
   * Which truck this browser is the screen for, if it is one at all.
   *
   * Read here because it decides what the route below the form should say, and
   * the two answers are addressed to different people:
   *
   *   paired      the tablet in a cab, sitting on the login page because that is
   *               where the tenant URL lands. Its driver wants the keypad, and
   *               the plate is worth showing — it confirms they are looking at
   *               their own truck's screen before they type anything.
   *   not paired   a phone or a laptop. The same link leads to a pairing form
   *               asking for a code from the office, which is a dead end for a
   *               driver and the right screen for whoever is installing a
   *               tablet — so it is offered quietly, and worded for them.
   *
   * A single button for both cases would have to be wrong for one of them.
   */
  const pairedTruck = getPairedTruck();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const onSubmit = async (data: LoginCredentials) => {
    try {
      const result = await dispatch(login(data)).unwrap();
      toast.success(t("login.loginSuccess"));
      const redirectPath =
        result.user?.role === "driver" ? "/driver-home" : "/dashboard";
      navigate(redirectPath, { replace: true });
    } catch (error) {
      // Error is handled by the effect above
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex">
      {/* Left Side - Hero Section */}
      <AuthHeroSection
        brandIcon={Building}
        brandTitle={t("login.hero.brandTitle")}
        mainTitle={t("login.hero.mainTitle")}
        highlightedTitle={t("login.hero.highlightedTitle")}
        description={t("login.hero.description")}
        features={[
          {
            icon: Truck,
            title: t("login.hero.feature1Title"),
            description: t("login.hero.feature1Desc"),
          },
          {
            icon: Shield,
            title: t("login.hero.feature2Title"),
            description: t("login.hero.feature2Desc"),
          },
          {
            icon: Users,
            title: t("login.hero.feature3Title"),
            description: t("login.hero.feature3Desc"),
          },
        ]}
      />
      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="mx-auto flex items-center justify-center gap-3 mb-4">
              <svg
                width="40"
                height="40"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect
                  x="22"
                  y="38"
                  width="2"
                  height="24"
                  fill="#C97A3C"
                  opacity="0.5"
                />
                <rect
                  x="27"
                  y="34"
                  width="2"
                  height="32"
                  fill="#C97A3C"
                  opacity="0.6"
                />
                <rect
                  x="32"
                  y="30"
                  width="3"
                  height="40"
                  fill="#C97A3C"
                  opacity="0.7"
                />
                <rect
                  x="38"
                  y="24"
                  width="3"
                  height="52"
                  fill="#C97A3C"
                  opacity="0.85"
                />
                <rect x="44" y="18" width="4" height="64" fill="#E89D5C" />
                <rect
                  x="51"
                  y="24"
                  width="3"
                  height="52"
                  fill="#C97A3C"
                  opacity="0.85"
                />
                <rect
                  x="57"
                  y="30"
                  width="3"
                  height="40"
                  fill="#C97A3C"
                  opacity="0.7"
                />
                <rect
                  x="63"
                  y="34"
                  width="2"
                  height="32"
                  fill="#C97A3C"
                  opacity="0.6"
                />
                <rect
                  x="68"
                  y="38"
                  width="2"
                  height="24"
                  fill="#C97A3C"
                  opacity="0.5"
                />
              </svg>
              <h1 className="text-2xl font-medium tracking-tight text-[#0B0F0E]">
                LiveCargo<span className="text-[#C97A3C]">.</span>
              </h1>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[rgba(11,15,14,0.08)] overflow-hidden shadow-sm">
            <div className="p-8 border-b border-[rgba(11,15,14,0.06)]">
              <h2 className="text-2xl font-medium tracking-tight text-[#0B0F0E] mb-2">
                {t("login.welcomeBack")}
              </h2>
              <p className="text-[#8A9590]">{t("login.signInSubtitle")}</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-[#8A9590] mb-2">
                  {t("login.emailAddress")}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#8A9590]" />
                  <input
                    {...register("email")}
                    type="email"
                    className="w-full pl-12 pr-4 py-3.5 border border-[rgba(11,15,14,0.15)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C97A3C] focus:border-transparent transition-all duration-200 bg-[#FFFFFF] focus:bg-white text-[#0B0F0E] placeholder:text-[#8A9590]"
                    placeholder={t("login.emailPlaceholder")}
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-[#E24B4A] flex items-center">
                    <span className="w-1 h-1 bg-[#E24B4A] rounded-full mr-2"></span>
                    {translateValidation(errors.email.message || "")}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium uppercase tracking-widest text-[#8A9590] mb-2">
                  {t("login.password")}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#8A9590]" />
                  <input
                    {...register("password")}
                    type={showPassword ? "text" : "password"}
                    className="w-full pl-12 pr-12 py-3.5 border border-[rgba(11,15,14,0.15)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C97A3C] focus:border-transparent transition-all duration-200 bg-[#FFFFFF] focus:bg-white text-[#0B0F0E] placeholder:text-[#8A9590]"
                    placeholder={t("login.passwordPlaceholder")}
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 rounded-lg hover:bg-[#F5F4F0] transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-[#8A9590]" />
                    ) : (
                      <Eye className="h-5 w-5 text-[#8A9590]" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-[#E24B4A] flex items-center">
                    <span className="w-1 h-1 bg-[#E24B4A] rounded-full mr-2"></span>
                    {translateValidation(errors.password.message || "")}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-[#C97A3C] hover:text-[#B86B30] transition-colors"
                >
                  {t("login.forgotPassword")}
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#C97A3C] hover:bg-[#B86B30] text-white py-3.5 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium shadow-sm"
                isLoading={loading}
                disabled={loading}
              >
                {loading ? t("login.signingIn") : t("login.signIn")}
              </Button>

              {/* ── The way onto a truck's screen ───────────────────────────
                  Reachable only by typing the URL until now, which is no way to
                  ship a tablet to a yard. */}
              {pairedTruck ? (
                <div className="pt-2 border-t border-[rgba(11,15,14,0.06)]">
                  <button
                    type="button"
                    onClick={() => navigate(DEVICE_ROUTE)}
                    className="mt-4 w-full flex items-center justify-center gap-2 py-3.5 rounded-lg border border-[#C97A3C] text-[#C97A3C] font-medium hover:bg-[#FCF6F1] transition-colors"
                  >
                    <Truck className="h-5 w-5" />
                    {t("login.truck.pinButton")}
                  </button>
                  {/* The truck's name, so a driver climbing into a cab can tell
                      this is their truck's screen before entering anything. */}
                  <p className="mt-2 text-center text-xs uppercase tracking-widest text-[#8A9590]">
                    {vehicleLabel(pairedTruck)}
                  </p>
                </div>
              ) : (
                <div className="pt-2 border-t border-[rgba(11,15,14,0.06)]">
                  <button
                    type="button"
                    onClick={() => navigate(DEVICE_ROUTE)}
                    className="mt-4 w-full text-center text-sm font-medium text-[#8A9590] hover:text-[#C97A3C] transition-colors"
                  >
                    {t("login.truck.setupLink")}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
